// ResumeEditorHelpers.js

/**
 * Parse an HTML string into a Document.
 * @param {string} html 
 * @returns {Document}
 */
export const parseDoc = (html) => new DOMParser().parseFromString(html, "text/html");

/**
 * Get the outerHTML of the Document's root element.
 * @param {Document} doc 
 * @returns {string}
 */
export const getDocHtml = (doc) => doc.documentElement.outerHTML;

/**
 * Escape HTML special characters.
 * @param {string} text 
 * @returns {string}
 */
export const escapeHtml = (text) => {
  if (!text) return "";
  return text.toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/**
 * Format section title (e.g., "contact_information" -> "Contact Information")
 * @param {string} sectionName 
 * @returns {string}
 */
export function formatSectionTitle(str) {
    // "awards_and_achievements" => "Awards And Achievements"
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }  

/**
 * Update font family.
 * @param {string} resumeHtml 
 * @param {string} font 
 * @returns {string} Updated resumeHtml.
 */
export const updateFont = (resumeHtml, font) => {
  const doc = parseDoc(resumeHtml);
  const oldStyleTag = doc.head.querySelector("#font-style");
  if (oldStyleTag) oldStyleTag.remove();
  const styleTag = doc.createElement("style");
  styleTag.id = "font-style";
  styleTag.innerHTML = `body { font-family: ${font} !important; }`;
  doc.head.appendChild(styleTag);
  return getDocHtml(doc);
};

/**
 * Update font size.
 * @param {string} resumeHtml 
 * @param {string} size 
 * @returns {string} Updated resumeHtml.
 */
export const updateFontSize = (resumeHtml, size) => {
  const doc = parseDoc(resumeHtml);
  const oldStyleTag = doc.head.querySelector("#font-size-style");
  if (oldStyleTag) oldStyleTag.remove();
  const styleTag = doc.createElement("style");
  styleTag.id = "font-size-style";
  styleTag.innerHTML = `body { font-size: ${size} !important; }`;
  doc.head.appendChild(styleTag);
  return getDocHtml(doc);
};

/**
 * Update theme color.
 * @param {string} resumeHtml 
 * @param {string} color 
 * @returns {string} Updated resumeHtml.
 */
export function updateThemeColor(newColor, resumeHtml) {
  // Update the live document's CSS variable
  document.documentElement.style.setProperty('--theme-color', newColor);
  console.log("Document theme color updated to:", newColor);

  // Parse the resumeHTML into a DOM Document
  const doc = parseDoc(resumeHtml);
  
  // Look for a <style> element that defines the --theme-color variable.
  // This assumes your template has a <style> block in the head.
  const styleEl = doc.querySelector("style");
  if (styleEl) {
    // Use a regex to find the --theme-color definition and replace it.
    // This regex matches something like: --theme-color: #2c7b38;
    styleEl.textContent = styleEl.textContent.replace(
      /(--theme-color:\s*)(#[0-9a-fA-F]{6})/,
      `$1${newColor}`
    );
    console.log("Updated style block with new theme color:", newColor);
  } else {
    console.log("No style block found in resumeHTML to update.");
  }

  // Return the updated HTML string so you can update your state.
  return getDocHtml(doc);
}



/**
 * Update a specific contact field in the contact_information section.
 * Looks for elements by class name.
 * @param {string} resumeHtml 
 * @param {string} field - e.g., "name", "job_title", "email", "phone", "location", "linkedIn", "website"
 * @param {string} newValue 
 * @returns {string} Updated resumeHtml.
 */
// In ResumeEditorHelpers.js
export const updateContactField = (resumeHtml, field, newValue) => {
    const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
    const contactSection = doc.querySelector("#contact_information");
    if (!contactSection) {
      console.warn("No #contact_information found in template");
      return resumeHtml;
    }
    switch(field) {
      case "name":
        {
          const el = contactSection.querySelector(".user-name");
          if (el) el.textContent = newValue;
        }
        break;
      case "job_title":
        {
          const el = contactSection.querySelector(".user-title");
          if (el) el.textContent = newValue;
        }
        break;
        case "phone":
          const phoneEl = contactSection.querySelector(".user-phone");
          if (phoneEl) phoneEl.textContent = newValue;
          break;
        
        case "email":
          const emailEl = contactSection.querySelector(".user-email-link");
          if (emailEl) {
            emailEl.textContent = newValue;
            emailEl.href = "mailto:" + newValue;
          }
          break;
        
        case "location":
          const locEl = contactSection.querySelector(".user-location");
          if (locEl) locEl.textContent = newValue;
          break;
        
        case "linkedIn":
          const linkedInEl = contactSection.querySelector(".user-linkedin-link");
          if (linkedInEl) {
            linkedInEl.textContent = newValue;
            linkedInEl.href = newValue;
          }
          break;        
      case "website":
        {
          const a = contactSection.querySelector(".user-website-link") || contactSection.querySelector(".user-website a");
          if (a) {
            a.textContent = newValue;
            a.href = newValue;
          }
        }
        break;
      default:
        console.warn("Unknown contact field:", field);
    }
    return doc.documentElement.outerHTML;
  };
  

/**
 * Updates a simple “string-only” section while preserving its <h3> heading.
 * 
 * @param {Document} doc - The parsed DOM document from your resumeHtml.
 * @param {string} sectionId - The ID of the section to update, e.g. "summary".
 * @param {string} newText - The new text content to place in the section.
 */
export function updateSimpleStringSection(doc, sectionId, newText) {
    const sectionEl = doc.querySelector(`#${sectionId}`);
    if (!sectionEl) {
      console.warn(`No #${sectionId} found in the template.`);
      return;
    }
  
    // Keep the first heading (h1, h2, or h3) instead of only h3
    const heading = sectionEl.querySelector("h1, h2, h3");
    const children = sectionEl.querySelectorAll("*:not(h1):not(h2):not(h3)");
    children.forEach(child => child.remove());
  
    // 3) Create a new paragraph with the updated text
    const p = doc.createElement("p");
    p.className = "simple-text"; // optional, for styling or identification
    p.textContent = newText;
  
    // 4) Append it back to the section
    sectionEl.appendChild(p);
  }
  

/**
 * Update a list-only section (e.g., skills, awards, languages) by replacing the <ul> content.
 * @param {string} resumeHtml 
 * @param {string} sectionId - e.g., "skills"
 * @param {string[]} itemsArray - The new array of items.
 * @param {string} ulClassName - (Optional) The class name to target the specific <ul>
 * @returns {string} Updated resumeHtml.
 */
export const updateListSection = (resumeHtml, sectionId, itemsArray, ulClassName = "skills-list") => {
    const doc = parseDoc(resumeHtml);
    let sectionEl = doc.querySelector(`#${sectionId}`);
    
    // If the section doesn't exist, create it with an <h2> header.
    if (!sectionEl) {
      sectionEl = doc.createElement("section");
      sectionEl.id = sectionId;
      
      const heading = doc.createElement("h2");
      heading.textContent = formatSectionTitle(sectionId);
      sectionEl.appendChild(heading);
      
      // Append the new section to a default container, e.g., #content or the document body.
      const contentContainer = doc.querySelector("#content") || doc.body;
      contentContainer.appendChild(sectionEl);
    }
    
    // Find the <ul> with the provided class name, or create one if it doesn't exist.
    let ul = sectionEl.querySelector("ul." + ulClassName) || sectionEl.querySelector("ul");
    if (!ul) {
      ul = doc.createElement("ul");
      ul.className = ulClassName;
      sectionEl.appendChild(ul);
    }
    
    // Clear existing list items and add new ones.
    ul.innerHTML = "";
    itemsArray.forEach(item => {
      const li = doc.createElement("li");
      li.textContent = item;
      ul.appendChild(li);
    });
    
    return getDocHtml(doc);
  };

  // New helper for updating the skills section when using ratings.
  export function updateSkillsSection(resumeHtml, skillsArray) {
    const doc = parseDoc(resumeHtml);
    let sectionEl = doc.querySelector("#skills");
  
    if (!sectionEl) {
      sectionEl = doc.createElement("section");
      sectionEl.id = "skills";
  
      const heading = doc.createElement("h2");
      heading.textContent = formatSectionTitle("skills");
      sectionEl.appendChild(heading);
  
      const contentContainer = doc.querySelector("#content") || doc.body;
      contentContainer.appendChild(sectionEl);
    }
  
    let ul = sectionEl.querySelector("ul.skills-list");
    if (!ul) {
      ul = doc.createElement("ul");
      ul.className = "skills-list";
      sectionEl.appendChild(ul);
    }
  
    ul.innerHTML = "";
  
    skillsArray.forEach(skill => {
      const li = doc.createElement("li");
      li.className = "skill-bar";
  
      const spanName = doc.createElement("span");
      spanName.className = "skill-name";
      spanName.textContent = skill.name;
      li.appendChild(spanName);
  
      const spanLevel = doc.createElement("span");
      spanLevel.className = "skill-level";
  
      const numericProficiency = parseInt(skill.proficiency, 10) || 0;
      const fillPercentage = numericProficiency * 20; // 1 → 20%, 5 → 100%
  
      const spanFill = doc.createElement("span");
      spanFill.className = "skill-fill";
      spanFill.style.width = `${fillPercentage}%`;
      spanLevel.appendChild(spanFill);
  
      li.appendChild(spanLevel);
      ul.appendChild(li);
    });
  
    return getDocHtml(doc);
  }
  

  export function updateSkillsSectionTemplate3(resumeHtml, skillsArray) {
    const doc = parseDoc(resumeHtml);
    let sectionEl = doc.querySelector("#skills");
  
    // If no <section id="skills">, create it
    if (!sectionEl) {
      sectionEl = doc.createElement("section");
      sectionEl.id = "skills";
  
      const heading = doc.createElement("h3");
      heading.textContent = "Key Skills";
      sectionEl.appendChild(heading);
  
      const contentContainer = doc.querySelector("#content") || doc.body;
      contentContainer.appendChild(sectionEl);
    }
  
    // Find or create the <ul class="skills-list">
    let ul = sectionEl.querySelector("ul.skills-list");
    if (!ul) {
      ul = doc.createElement("ul");
      ul.className = "skills-list";
      sectionEl.appendChild(ul);
    }
  
    // Clear existing list items
    ul.innerHTML = "";
  
    // Build each skill row
    skillsArray.forEach(skill => {
      // skill = { name: "JavaScript", proficiency: "3" } (for example)
      const li = doc.createElement("li");
      li.className = "skill-bar";
  
      // Skill name
      const spanName = doc.createElement("span");
      spanName.className = "skill-name";
      spanName.textContent = skill.name;
      li.appendChild(spanName);
  
      // Container for squares
      const divSkillLevel = doc.createElement("div");
      divSkillLevel.className = "skill-level";
  
      // Convert proficiency to a number
      const numericProficiency = parseInt(skill.proficiency, 10) || 0;
  
      // Create five squares
      for (let i = 1; i <= 5; i++) {
        const square = doc.createElement("div");
        // If the skill's proficiency is >= i, fill the square
        if (numericProficiency >= i) {
          square.classList.add("filled");
        }
        divSkillLevel.appendChild(square);
      }
  
      li.appendChild(divSkillLevel);
      ul.appendChild(li);
    });
  
    return getDocHtml(doc);
  }
  
  

/**
 * Updates the work experience section in the provided document.
 * This function removes existing entry blocks (except the header) and rebuilds them using
 * granular updates so that each field is placed in its own element.
 *
 * @param {Document} doc - The parsed DOM document from resumeHtml.
 * @param {Array} experiences - An array of experience objects.
 *   Each object should have:
 *     - title (string)
 *     - company (string)
 *     - startDate (string)
 *     - endDate (string)
 *     - location (string)
 *     - responsibilities (array of strings)
 */
export const updateWorkExperienceSection = (doc, experiences) => {
  const workSection = doc.querySelector("#work_experience");
  if (!workSection) {
    console.warn("No #work_experience section found.");
    return;
  }
  // Preserve the header (assumed to be the first h3 element)
  const header = workSection.querySelector("h3");
  // Remove any existing entries (elements with class "entry")
  const existingEntries = workSection.querySelectorAll(".entry");
  existingEntries.forEach(entry => entry.remove());

  experiences.forEach(exp => {
    const entryDiv = doc.createElement("div");
    entryDiv.className = "entry";

    // Create separate elements for title and company
    const titleEl = doc.createElement("p");
    titleEl.className = "entry-title";
    const titleSpan = doc.createElement("span");
    titleSpan.className = "exp-title";
    titleSpan.textContent = exp.title;
    const companySpan = doc.createElement("span");
    companySpan.className = "exp-company";
    companySpan.textContent = exp.company;
    titleEl.appendChild(titleSpan);
    titleEl.appendChild(doc.createTextNode(" - "));
    titleEl.appendChild(companySpan);
    entryDiv.appendChild(titleEl);

    // Create separate element for dates
    const datesEl = doc.createElement("p");
    datesEl.className = "entry-dates";
    const startSpan = doc.createElement("span");
    startSpan.className = "exp-start";
    startSpan.textContent = exp.startDate;
    const endSpan = doc.createElement("span");
    endSpan.className = "exp-end";
    endSpan.textContent = exp.endDate;
    datesEl.appendChild(startSpan);
    datesEl.appendChild(doc.createTextNode(" - "));
    datesEl.appendChild(endSpan);
    entryDiv.appendChild(datesEl);

    // Location
    const locationEl = doc.createElement("p");
    locationEl.className = "entry-location";
    locationEl.textContent = exp.location;
    entryDiv.appendChild(locationEl);

    // Responsibilities as a list (if provided)
    if (exp.responsibilities && Array.isArray(exp.responsibilities)) {
      const ul = doc.createElement("ul");
      ul.className = "responsibilities-list"; // Apply class for styling
      exp.responsibilities.forEach(res => {
        const li = doc.createElement("li");
        li.textContent = res;
        ul.appendChild(li);
      });
      entryDiv.appendChild(ul);
    }

    workSection.appendChild(entryDiv);
  });
};

  
  /**
   * Updates the education section in the provided document.
   * This function removes existing education entries and rebuilds them,
   * updating each field separately (degree, institution, dates, description).
   *
   * @param {Document} doc - The parsed DOM document from resumeHtml.
   * @param {Array} educations - An array of education objects.
   *   Each object should have:
   *     - degree (string)
   *     - institution (string)
   *     - startDate (string)
   *     - endDate (string)
   *     - description (string)
   */
  export const updateEducationSection = (doc, educations) => {
    const eduSection = doc.querySelector("#education");
    if (!eduSection) {
      console.warn("No #education section found.");
      return;
    }
    // Preserve the header (assumed to be the first h2 element)
    const header = eduSection.querySelector("h2");
    // Remove any existing entries (elements with class "entry")
    const existingEntries = eduSection.querySelectorAll(".entry");
    existingEntries.forEach(entry => entry.remove());
  
    educations.forEach(edu => {
      const entryDiv = doc.createElement("div");
      entryDiv.className = "entry";
  
      // Create a single paragraph for degree and institution
      const entryTitle = doc.createElement("p");
      entryTitle.className = "entry-title";
      // This matches the template: degree and institution separated by " - "
      entryTitle.textContent = `${edu.degree} - ${edu.institution}`;
      entryDiv.appendChild(entryTitle);
  
      // Create a paragraph for the graduation year using the "entry-details" class.
      const entryDetails = doc.createElement("p");
      entryDetails.className = "entry-details";
      // Using edu.year; if your data uses a different property (like startDate/endDate), update as needed.
      entryDetails.textContent = `Graduated: ${edu.year || ""}`;
      entryDiv.appendChild(entryDetails);
  
      // Append the new entry to the education section.
      eduSection.appendChild(entryDiv);
    });
  };
  

  export const updateCertificationsSection = (resumeHtml, certifications) => {
    const doc = parseDoc(resumeHtml);
    let sectionEl = doc.querySelector("#certifications");
  
    // If the section doesn't exist, create it and append to #content or body.
    if (!sectionEl) {
      sectionEl = doc.createElement("section");
      sectionEl.id = "certifications";
  
      const heading = doc.createElement("h2");
      heading.textContent = formatSectionTitle("certifications");
      sectionEl.appendChild(heading);
  
      // Append the new section to #content if it exists, otherwise to the document body.
      const contentContainer = doc.querySelector("#content") || doc.body;
      contentContainer.appendChild(sectionEl);
    }
  
    // Find the <ul> with the class "certifications-list", or create one if it doesn't exist.
    let ul = sectionEl.querySelector("ul.certifications-list") || sectionEl.querySelector("ul");
    if (!ul) {
      ul = doc.createElement("ul");
      ul.className = "certifications-list";
      sectionEl.appendChild(ul);
    }
  
    // Clear existing list items.
    ul.innerHTML = "";
  
    // Create a new <li> for each certification with detailed descriptors.
    certifications.forEach(cert => {
      const li = doc.createElement("li");
      li.className = "certification-item";
      li.innerHTML = `
        <p class="entry-title"><strong>${escapeHtml(cert.certificationName)}</strong></p>
        <p>Issued by: ${escapeHtml(cert.issuingOrganization)}</p>
        <p>Obtained in: ${escapeHtml(cert.issueDate)}</p>
        <p>Expires in: ${escapeHtml(cert.expirationDate)}</p>
      `;
      ul.appendChild(li);
    });
  
    return getDocHtml(doc);
  };
  
  export const updateProjectsSection = (resumeHtml, projects) => {
    // 1) Parse the incoming HTML string into a Document.
    const doc = parseDoc(resumeHtml);
  
    // 2) Find or create a <section id="projects">
    let projectsSection = doc.querySelector("#projects");
    if (!projectsSection) {
      console.log("updateProjectsSection: #projects section not found, creating new one.");
      projectsSection = doc.createElement("section");
      projectsSection.id = "projects";
  
      // Create and append the heading.
      const heading = doc.createElement("h2");
      heading.textContent = formatSectionTitle("projects");
      projectsSection.appendChild(heading);
  
      // Append the new section to #content if it exists; otherwise, to the document body.
      const contentContainer = doc.querySelector("#content") || doc.body;
      contentContainer.appendChild(projectsSection);
    } else {
      console.log("updateProjectsSection: Found existing #projects section.");
    }
  
    // 3) Remove existing project entries while preserving the header.
    // Here, we assume that the first child is the header.
    const children = Array.from(projectsSection.children);
    console.log("updateProjectsSection: Existing children before removal:", children);
    children.forEach((child, idx) => {
      if (idx !== 0) { // preserve header at index 0
        console.log("updateProjectsSection: Removing child at index", idx);
        child.remove();
      }
    });
  
    // 4) Rebuild the projects list from scratch.
    projects.forEach((proj, idx) => {
      console.log("updateProjectsSection: Adding project at index", idx, proj);
      const entryDiv = doc.createElement("div");
      entryDiv.className = "entry";
  
      // Create the title element with class "entry-title"
      const titleP = doc.createElement("p");
      titleP.className = "entry-title";
      titleP.textContent = escapeHtml(proj.projectTitle);
      entryDiv.appendChild(titleP);
  
      // Create the description element.
      const descP = doc.createElement("p");
      descP.textContent = escapeHtml(proj.description);
      entryDiv.appendChild(descP);
  
      // Append the new entry to the projects section.
      projectsSection.appendChild(entryDiv);
    });
  
    const newHtml = getDocHtml(doc);
    console.log("updateProjectsSection: Returning updated HTML:", newHtml);
    // 5) Return the updated HTML string.
    return newHtml;
  };
  